import { Button } from '@storeblocks/button';
import { DatePickerSelectedValue } from '@storeblocks/datepicker';
import { format } from 'date-fns';
import { type FC, useCallback, useMemo } from 'react';
import styled from 'styled-components';

import { useDocumentCategoriesQuery } from '@/api/documentArchive/documentArchiveApi';
import { DocumentCategory } from '@/api/documentArchive/models/DocumentCategory';
import { Show } from '@/components/Show';
import { DateSearch } from '@/components/Toolbar/DateSearch';
import { DownloadButton } from '@/components/Toolbar/DownloadButton';
import { DownloadErrorAlert } from '@/components/Toolbar/DownloadErrorAlert';
import { useSearchParams } from '@/components/Toolbar/hooks/useSearchParams';
import { SelectSearch } from '@/components/Toolbar/SelectSearch';
import { TextSearch } from '@/components/Toolbar/TextSearch';
import { WithGap } from '@/components/WithGap';
import { useHandleAsyncOperation } from '@/hooks/useHandleAsyncError';
import { useTexts } from '@/hooks/useTexts';
import {
  documentsDownloadUrl,
  downloadFiles,
} from '@/pages/Archives/commonLogic';
import { media } from '@/styles/variables';
import { TrackingElement, useGetTrackId } from '@/tracking';
import { convertStringToEnum } from '@/util/convertStringToEnum';

import { useDocumentsArchiveContext } from './DocumentsArchiveContext';
import { convertDocumentCategoriesToOptions } from './documentsArchiveLogic';

interface SearchParams {
  query?: string;
  date?: string;
  category?: string;
}

export const Toolbar: FC = () => {
  const texts = useTexts();
  const getTrackId = useGetTrackId();

  const { setDocumentFilesParams: setDocumentFileArgs } =
    useDocumentsArchiveContext();

  const { checkedDocumentFiles, setShowUploadFileModal } =
    useDocumentsArchiveContext();

  const documentCategoriesQuery = useDocumentCategoriesQuery();

  const documentCategoryOptions = useMemo(
    () =>
      convertDocumentCategoriesToOptions(documentCategoriesQuery.data!, texts, {
        includeAllCategoriesCategory: true,
      }),
    [documentCategoriesQuery.data, texts],
  );

  const onSearchParamsChange = useCallback(
    (searchParams: SearchParams) => {
      setDocumentFileArgs({
        category:
          searchParams.category && searchParams.category !== 'ALL_CATEGORIES'
            ? convertStringToEnum(searchParams.category, DocumentCategory)
            : undefined,
        startDate: searchParams.date,
        endDate: searchParams.date,
        name: searchParams.query,
      });
    },
    [setDocumentFileArgs],
  );

  const [handleDownloadClick, , downloadError] = useHandleAsyncOperation(
    async () => {
      await downloadFiles(documentsDownloadUrl(), [...checkedDocumentFiles]);
    },
  );
  const searchParams = useSearchParams<SearchParams>(onSearchParamsChange);

  const updateTextSearch = (query: string): void => {
    searchParams.update((state) => {
      state.query = query;
    });
  };

  const updateCategorySearch = (category: string): void => {
    searchParams.update((state) => {
      state.category = category;
    });
  };

  const updateDateSearch = ({ date }: DatePickerSelectedValue): void => {
    const dateString = date ? format(date, 'yyyy-MM-dd') : '';

    searchParams.update((state) => {
      state.date = dateString;
    });
  };

  return (
    <>
      <Wrapper>
        <TextSearch
          onChange={updateTextSearch}
          label={texts.documentsArchivePage.searchForDocuments}
        />
        <SelectSearch
          id="search-by-category"
          onChange={updateCategorySearch}
          label={texts.category.text}
          options={documentCategoryOptions}
        />
        <DateSearch
          onChange={updateDateSearch}
          label={texts.documentsArchivePage.datePublished}
        />
        <WithGap gap="16" direction="row" className="toolbar-buttons">
          <Button
            onClick={() => setShowUploadFileModal(true)}
            id="upload-file-button"
            data-trackid={getTrackId(
              'open-upload-file-modal',
              TrackingElement.Button,
            )}
          >
            {texts.documentsArchivePage.upload}
          </Button>
          <DownloadButton
            onClick={handleDownloadClick}
            numberOfFilesToDownload={checkedDocumentFiles.size}
          />
        </WithGap>
      </Wrapper>

      <Show when={downloadError}>
        <DownloadErrorAlert />
      </Show>
    </>
  );
};

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr auto;
  // Rows containing input fields must have a minimum height of 110px to allow room for an error alert
  // to be displayed below the input fields.
  grid-template-rows: minmax(110px, auto);
  gap: 16px;

  & > .toolbar-buttons {
    // Align the download button with the input fields
    margin-top: 30px;
  }

  @media ${media.maxContentWidthAndBelow} {
    grid-template-columns: 1fr 1fr 1fr;
    // Rows containing input fields must have a minimum height of 110px to allow room for an error alert
    // to be displayed below the input fields.
    grid-template-rows: minmax(110px, auto) auto;

    & > .toolbar-buttons {
      margin-top: 0;

      grid-row: 2;
      grid-column: 1;
    }
  }

  @media ${media.tabletAndBelow} {
    grid-template-columns: 1fr;
    // Rows containing input fields must have a minimum height of 110px to allow room for an error alert
    // to be displayed below the input fields.
    grid-template-rows: repeat(3, minmax(110px, auto)) auto;

    & > .toolbar-buttons {
      grid-row: 4;
    }
  }
`;
